import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Ruler, Save, Sparkles, Upload, Loader2, FileText, Download,
  Plus, Trash2, Wand2, Calculator, Paperclip, X, History, ChevronDown, ChevronUp,
  AlertTriangle, RefreshCcw,
} from "lucide-react";

/**
 * /takeoffs/:takeoffId — Editable structured takeoff.  Each section is a
 * table (or a bullet list for assumptions/exclusions/verification).  Users
 * can regenerate with a new prompt, upload plans, edit cells inline, add or
 * delete rows, download a PDF, or convert to a priced estimate.
 */
export default function TakeoffDetail() {
  const { takeoffId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pdfPending, setPdfPending] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [tradesInput, setTradesInput] = useState("");
  const [includePricing, setIncludePricing] = useState(false);
  const [taperedSystem, setTaperedSystem] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);
  const [openRevisionId, setOpenRevisionId] = useState(null);
  const [genProgress, setGenProgress] = useState(null);   // {status, progress}
  const [ingestingFromTakeoff, setIngestingFromTakeoff] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const ingestPollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/takeoffs/${takeoffId}`);
      setDoc(r.data);
      setPrompt(r.data.prompt || r.data.scope_summary || "");
      setTradesInput((r.data.trades || ["all"]).join(", "));
      setTaperedSystem(!!r.data.tapered_system);
    } catch (e) {
      toast.error("Could not load takeoff");
      navigate("/takeoffs");
    } finally {
      setLoading(false);
    }
  };

  const loadRevisions = async () => {
    try {
      const r = await api.get(`/takeoffs/${takeoffId}/revisions`);
      setRevisions(r.data.items || []);
    } catch (err) {
      // Non-fatal — the revisions panel just stays empty.  Log so we can spot
      // regressions in the revisions endpoint without spamming toasts.
      console.error("loadRevisions failed:", err);
    }
  };

  // Iter-138.4: "Run Ingest Now" from the takeoff page — no need to
  // detour to the RFP page just because ingest hasn't fired yet.
  // After the RFP endpoint returns, we poll the takeoff GET (which
  // re-runs the idempotent _autoseed_takeoff_from_ingest helper) until
  // its ingest_status flips to "ready" or "no_files"/"n/a".
  const ingestFromTakeoff = async () => {
    if (ingestingFromTakeoff || !doc?.notice_id) return;
    setIngestingFromTakeoff(true);
    try {
      const r = await api.post(`/opportunities/${doc.notice_id}/ingest-attachments`, {}, { timeout: 240000 });
      toast.success(`Read ${r.data.files_processed || 0} attachment(s) from SAM.gov — auto-seeding takeoff…`);
      // Poll the takeoff GET; each read re-runs the auto-seed helper so
      // once the ingest lands the attachments materialize on the doc.
      if (ingestPollRef.current) clearInterval(ingestPollRef.current);
      let _tries = 0;
      ingestPollRef.current = setInterval(async () => {
        _tries += 1;
        try {
          const g = await api.get(`/takeoffs/${takeoffId}`);
          setDoc(g.data);
          const _atts = (g.data.attachments || []).length;
          const _status = g.data.ingest_status;
          if (_atts > 0 || _status === "no_files" || _status === "n/a" || _tries >= 20) {
            clearInterval(ingestPollRef.current);
            ingestPollRef.current = null;
            setIngestingFromTakeoff(false);
            if (_atts > 0) {
              toast.success(`Takeoff now has ${_atts} attachment(s) from the SAM.gov ingest.`);
            } else if (_status === "no_files") {
              toast.message("SAM.gov notice returned no downloadable attachments — upload plans manually.");
            }
          }
        } catch (_) { /* silent — try again next tick */ }
      }, 3000);
    } catch (e) {
      setIngestingFromTakeoff(false);
      toast.error(e?.response?.data?.detail || "SAM.gov ingest failed");
    }
  };

  useEffect(() => () => {
    // Clean up the ingest poller on unmount so we don't leak the interval.
    if (ingestPollRef.current) clearInterval(ingestPollRef.current);
  }, []);

  useEffect(() => {
    load().then(() => {
      // If the user reloads the page mid-generation, seamlessly re-attach
      // the poller so they don't have to re-click Generate.  A `stalled`
      // status on mount means the previous run died (likely a container
      // restart) — surface the Cancel & Retry CTA immediately without a poll.
      api.get(`/takeoffs/${takeoffId}/generate/status`).then((r) => {
        const s = r?.data?.status;
        if (s === "stalled") {
          setGenerating(true);
          setGenProgress(r.data);
          return;
        }
        if (s === "queued" || s === "reading" || s === "thinking") {
          setGenerating(true);
          setGenProgress(r.data);
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = setInterval(async () => {
            try {
              const p = await api.get(`/takeoffs/${takeoffId}/generate/status`);
              setGenProgress(p.data);
              if (p.data.status === "done") {
                clearInterval(pollRef.current);
                pollRef.current = null;
                await load();
                await loadRevisions();
                setGenerating(false);
                setGenProgress(null);
                toast.success("Generation complete");
              } else if (p.data.status === "error" || p.data.status === "stalled") {
                clearInterval(pollRef.current);
                pollRef.current = null;
                // Keep the panel visible so the user sees the "Cancel & retry" CTA.
                if (p.data.status === "stalled") {
                  toast.error("Generation stalled — cancel and retry", { duration: 8000 });
                } else {
                  toast.error(p.data.error || "Generation failed");
                  setGenerating(false);
                  setGenProgress(null);
                }
              }
            } catch (err) {
              // Transient network hiccup — keep polling; log 5xx for ops.
              const s = err?.response?.status;
              if (s && s >= 500) console.error("takeoff status poll failed:", err);
            }
          }, 2000);
        }
      }).catch(() => {});
    });
    loadRevisions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeoffId]);

  const patchLocal = (patch) => setDoc((d) => ({ ...d, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/takeoffs/${takeoffId}`, {
        project_name: doc.project_name,
        address: doc.address,
        client_name: doc.client_name,
        scope_summary: doc.scope_summary,
        trades: tradesInput.trim().toLowerCase() === "all"
          ? ["all"]
          : tradesInput.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
        sections: doc.sections,
      });
      setDoc(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const [maxVisionConfirmOpen, setMaxVisionConfirmOpen] = useState(false);

  const generate = async ({ maxVision = false } = {}) => {
    setGenerating(true);
    setGenProgress({ status: "queued", progress: maxVision ? "Queuing (MAX VISION)…" : "Queuing…" });
    try {
      const trades = tradesInput.trim().toLowerCase() === "all"
        ? ["all"]
        : tradesInput.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
      // Kick off — returns instantly with a job_id.  The big Claude call runs
      // in a background task on the server so Cloudflare's 100s cap doesn't
      // sever the request mid-thought.
      const r = await api.post(`/takeoffs/${takeoffId}/generate`, {
        prompt: prompt.trim() || null,
        trades,
        tapered_system: taperedSystem,
        include_pricing: includePricing,
        max_vision: maxVision,
      });
      if (r.data.already_running) {
        toast.info("A generation is already in progress — attaching to it.");
      }
      setGenProgress({ status: r.data.status || "queued", progress: "Reading attachments…" });
      // Poll every 2s
      pollRef.current && clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/takeoffs/${takeoffId}/generate/status`);
          setGenProgress(s.data);
          if (s.data.status === "done") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            const pages = s.data.plan_pages_analyzed || 0;
            toast.success(
              `Generated ${s.data.section_count || 0} sections` +
                (pages ? ` · read ${pages} plan page${pages === 1 ? "" : "s"}` : "")
            );
            await load();
            await loadRevisions();
            setGenerating(false);
            setGenProgress(null);
          } else if (s.data.status === "error") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            toast.error(s.data.error || "Generation failed — check the logs");
            setGenerating(false);
            setGenProgress(null);
          } else if (s.data.status === "stalled") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            // Leave `generating` true so the Cancel & Retry CTA stays visible.
            toast.error(s.data.error || "Generation stalled — cancel and retry", { duration: 8000 });
          }
        } catch (err) {
          const st = err?.response?.status;
          if (st && st >= 500) console.error("takeoff status poll failed:", err);
        }
      }, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue generation");
      setGenerating(false);
      setGenProgress(null);
    }
  };

  const cancelGeneration = async () => {
    try {
      await api.post(`/takeoffs/${takeoffId}/generate/cancel`);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setGenerating(false);
      setGenProgress(null);
      toast.success("Cancelled — you can retry now");
    } catch (err) {
      console.error("cancel takeoff generation failed:", err);
      toast.error("Could not cancel — try refreshing the page");
    }
  };

  // Clean up any active poll on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const uploadPlan = async (fileList) => {
    // Supports one file or many (multi-select / drag-drop).  ZIP archives are
    // expanded server-side into individual attachments.
    const arr = Array.from(fileList || []).filter(Boolean);
    if (!arr.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      arr.forEach((f) => fd.append("files", f));
      const r = await api.post(`/takeoffs/${takeoffId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const attached = r.data.attached || [];
      const skipped = r.data.skipped || [];
      if (attached.length) toast.success(`Attached ${attached.length} file${attached.length === 1 ? "" : "s"}`);
      if (skipped.length) toast.warning(`${skipped.length} skipped: ${skipped.map((s) => s.filename).slice(0, 3).join(", ")}${skipped.length > 3 ? "…" : ""}`);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      // Backend returns { detail: { error, skipped: [...] } } on 400.  Surface
      // the specific rejection reasons so the operator knows what to fix.
      const detail = e?.response?.data?.detail;
      const skipped = detail?.skipped;
      if (Array.isArray(skipped) && skipped.length) {
        const preview = skipped.slice(0, 3).map((s) => `${s.filename}: ${s.reason}`).join(" · ");
        toast.error(`Upload rejected — ${preview}${skipped.length > 3 ? ` (+${skipped.length - 3} more)` : ""}`, { duration: 10000 });
      } else {
        toast.error(detail?.error || detail || e?.message || "Upload failed");
      }
      console.error("takeoff upload failed:", e);
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (attachmentId) => {
    try {
      await api.delete(`/takeoffs/${takeoffId}/attachments/${attachmentId}`);
      await load();
    } catch (err) {
      console.error("removeAttachment failed:", err);
      toast.error("Could not remove attachment");
    }
  };

  const downloadPdf = async () => {
    setPdfPending(true);
    try {
      const r = await api.get(`/takeoffs/${takeoffId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(doc.project_name || "takeoff").replace(/[^a-z0-9]+/gi, "_")}_takeoff.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("downloadPdf failed:", err);
      toast.error("PDF download failed");
    } finally {
      setPdfPending(false);
    }
  };

  const convertToEstimate = async () => {
    try {
      const r = await api.post(`/takeoffs/${takeoffId}/convert-to-estimate`);
      toast.success("Handing off to the Estimator…");
      navigate(`/opportunities/${r.data.notice_id}/estimate`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not convert");
    }
  };

  // Section editors --------------------------------------------------------
  const updateSection = (idx, patch) => {
    const next = [...doc.sections];
    next[idx] = { ...next[idx], ...patch };
    patchLocal({ sections: next });
  };
  const updateCell = (sIdx, rIdx, cIdx, val) => {
    const next = [...doc.sections];
    const rows = [...(next[sIdx].rows || [])];
    const row = [...(rows[rIdx] || [])];
    row[cIdx] = val;
    rows[rIdx] = row;
    next[sIdx] = { ...next[sIdx], rows };
    patchLocal({ sections: next });
  };
  const addRow = (sIdx) => {
    const next = [...doc.sections];
    const cols = next[sIdx].columns || [];
    const rows = [...(next[sIdx].rows || []), cols.map(() => "")];
    next[sIdx] = { ...next[sIdx], rows };
    patchLocal({ sections: next });
  };
  const deleteRow = (sIdx, rIdx) => {
    const next = [...doc.sections];
    const rows = [...(next[sIdx].rows || [])];
    rows.splice(rIdx, 1);
    next[sIdx] = { ...next[sIdx], rows };
    patchLocal({ sections: next });
  };
  const addNote = (sIdx) => {
    const next = [...doc.sections];
    next[sIdx] = { ...next[sIdx], notes: [...(next[sIdx].notes || []), ""] };
    patchLocal({ sections: next });
  };
  const updateNote = (sIdx, nIdx, val) => {
    const next = [...doc.sections];
    const notes = [...(next[sIdx].notes || [])];
    notes[nIdx] = val;
    next[sIdx] = { ...next[sIdx], notes };
    patchLocal({ sections: next });
  };
  const deleteNote = (sIdx, nIdx) => {
    const next = [...doc.sections];
    const notes = [...(next[sIdx].notes || [])];
    notes.splice(nIdx, 1);
    next[sIdx] = { ...next[sIdx], notes };
    patchLocal({ sections: next });
  };
  const deleteSection = (sIdx) => {
    if (!window.confirm("Delete this section?")) return;
    const next = [...doc.sections];
    next.splice(sIdx, 1);
    patchLocal({ sections: next });
  };

  if (loading || !doc) {
    return (
      <div className="p-10 text-center text-slate-500 text-sm">
        <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin" /> Loading takeoff…
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="takeoff-detail-page">
      <Link
        to="/takeoffs"
        className="inline-flex items-center gap-1 text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-orange-600 mb-4"
        data-testid="takeoff-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> BACK TO TAKEOFFS
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30 flex-shrink-0">
            <Ruler className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-mono font-bold tracking-widest text-orange-600 flex items-center gap-2 flex-wrap">
              <span>// QUANTITY TAKEOFF · REV {doc.revision || 1}</span>
              {(doc.plan_pages_analyzed || 0) > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 text-[9px]"
                  data-testid="takeoff-vision-badge"
                  title="Number of plan pages the Takeoff Agent visually analyzed"
                >
                  VISION · {doc.plan_pages_analyzed} PAGE{doc.plan_pages_analyzed === 1 ? "" : "S"}
                </span>
              )}
            </div>
            <input
              value={doc.project_name || ""}
              onChange={(e) => patchLocal({ project_name: e.target.value })}
              className="font-display font-black text-2xl md:text-3xl text-slate-900 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-orange-500 focus:outline-none w-full"
              data-testid="takeoff-title"
            />
            <div className="text-xs text-slate-500 mt-1 flex gap-2 flex-wrap">
              <input
                value={doc.address || ""}
                onChange={(e) => patchLocal({ address: e.target.value })}
                placeholder="Address"
                className="bg-transparent border-b border-transparent hover:border-slate-300 focus:border-orange-500 focus:outline-none"
                data-testid="takeoff-address"
              />
              ·
              <input
                value={doc.client_name || ""}
                onChange={(e) => patchLocal({ client_name: e.target.value })}
                placeholder="Client / architect"
                className="bg-transparent border-b border-transparent hover:border-slate-300 focus:border-orange-500 focus:outline-none"
                data-testid="takeoff-client"
              />
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="text-xs px-4 py-2.5 rounded-full font-mono font-bold tracking-widest bg-white border border-slate-300 hover:border-orange-500 hover:text-orange-600 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="takeoff-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            SAVE
          </button>
          <button
            onClick={downloadPdf}
            disabled={pdfPending}
            className="text-xs px-4 py-2.5 rounded-full font-mono font-bold tracking-widest bg-white border border-slate-300 hover:border-orange-500 hover:text-orange-600 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="takeoff-pdf"
          >
            {pdfPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            PDF
          </button>
          <button
            onClick={convertToEstimate}
            className="text-xs px-4 py-2.5 rounded-full font-mono font-bold tracking-widest bg-white border border-slate-300 hover:border-violet-500 hover:text-violet-600 inline-flex items-center gap-2"
            data-testid="takeoff-convert"
          >
            <Calculator className="w-3.5 h-3.5" />
            PRICE IT
          </button>
        </div>
      </div>

      {/* Generation panel */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 mb-6" data-testid="takeoff-gen-panel">
        <div className="text-[10px] font-mono font-bold tracking-widest text-orange-600 mb-3">
          // TAKEOFF AGENT · CLAUDE SONNET 4.5
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-2">
            <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1 block">
              SCOPE PROMPT
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder="Tell the agent about pitches, dimensions, options, and anything the plans don't spell out."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="takeoff-prompt"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1 block">
              TRADES
            </label>
            <input
              value={tradesInput}
              onChange={(e) => setTradesInput(e.target.value)}
              placeholder='roofing, framing, siding — or "all"'
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="takeoff-trades-input"
            />
            <label className="flex items-center gap-2 mt-3 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={includePricing}
                onChange={(e) => setIncludePricing(e.target.checked)}
                data-testid="takeoff-include-pricing"
              />
              Include pricing column (workspace price book)
            </label>
            <label className="flex items-start gap-2 mt-2 text-xs text-slate-600 cursor-pointer" data-testid="takeoff-tapered-label">
              <input
                type="checkbox"
                checked={taperedSystem}
                onChange={(e) => setTaperedSystem(e.target.checked)}
                className="mt-0.5"
                data-testid="takeoff-tapered-checkbox"
              />
              <span>
                <span className="block font-medium text-slate-800">Tapered insulation system</span>
                <span className="block text-[11px] text-slate-500">Runs an extra pass for low-slope TPO/EPDM/PVC — quantifies tapered field, average thickness, board-feet, crickets, and min R-value.</span>
              </span>
            </label>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => generate()}
            disabled={generating && genProgress?.status !== "stalled"}
            className="bf-pill text-xs px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="takeoff-generate"
          >
            {generating && genProgress?.status !== "stalled" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            {generating && genProgress?.status !== "stalled" ? "GENERATING…" : doc.sections?.length ? "REGENERATE" : "GENERATE TAKEOFF"}
          </button>
          {genProgress && (
            <div
              className={`text-xs inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                genProgress.status === "stalled"
                  ? "bg-rose-50 border-rose-200 text-rose-700"
                  : "bg-violet-50 border-violet-200 text-slate-600"
              }`}
              data-testid="takeoff-gen-progress"
            >
              {genProgress.status === "stalled"
                ? <span className="font-mono font-bold tracking-widest text-rose-700 text-[10px]">STALLED</span>
                : <>
                    <Loader2 className="w-3 h-3 animate-spin text-violet-600" />
                    <span className="font-mono font-bold tracking-widest text-violet-700 text-[10px]">
                      {(genProgress.status || "").toUpperCase()}
                    </span>
                  </>}
              <span className={genProgress.status === "stalled" ? "text-rose-700" : "text-slate-600"}>
                {genProgress.error || genProgress.progress || "Working…"}
              </span>
              {genProgress.status === "stalled" && (
                <button
                  onClick={cancelGeneration}
                  className="ml-2 text-[10px] font-mono font-bold tracking-widest bg-rose-500 hover:bg-rose-600 text-white px-2.5 py-1 rounded-full"
                  data-testid="takeoff-cancel-btn"
                >
                  CANCEL & RETRY
                </button>
              )}
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.docx,.xlsx,.xls,.csv,.zip"
            onChange={(e) => uploadPlan(e.target.files)}
            data-testid="takeoff-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="text-xs px-4 py-2.5 rounded-full font-mono font-bold tracking-widest bg-white border border-slate-300 hover:border-orange-500 hover:text-orange-600 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="takeoff-upload"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "UPLOADING" : "UPLOAD FILES / ZIP"}
          </button>
          {(doc.attachments || []).length > 0 && (
            <span className="text-xs text-slate-500" data-testid="takeoff-attach-count">
              {doc.attachments.length} attachment{doc.attachments.length === 1 ? "" : "s"}
              {doc.attachments.some((a) => a.source === "sam_gov_ingest") && (
                <span className="ml-1.5 text-emerald-600 font-mono font-bold">· auto-seeded from SAM.gov</span>
              )}
            </span>
          )}
          {/* Iter-138.3.b: surface ingest_status when 0 attachments so
              the operator knows exactly what state their notice is in. */}
          {(doc.attachments || []).length === 0 && doc.source_type === "federal" && doc.ingest_status && doc.ingest_status !== "n/a" && (
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-xs font-mono font-bold ${
                  doc.ingest_status === "waiting" ? "text-amber-600" :
                  doc.ingest_status === "not_ingested" ? "text-red-600" :
                  "text-slate-500"
                }`}
                data-testid={`takeoff-ingest-status-${doc.ingest_status}`}
              >
                {doc.ingest_status === "waiting" && `SAM.gov ingest still running · ${doc.ingest_progress || "refresh in a moment"}`}
                {doc.ingest_status === "not_ingested" && "Notice has no SAM.gov ingest yet"}
                {doc.ingest_status === "no_files" && "Notice has no SAM.gov attachments — upload plans manually"}
              </span>
              {doc.ingest_status === "not_ingested" && (
                <button
                  onClick={ingestFromTakeoff}
                  disabled={ingestingFromTakeoff}
                  className="text-xs px-2.5 py-1 rounded-md bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-60 disabled:cursor-not-allowed font-mono font-bold uppercase tracking-widest flex items-center gap-1"
                  data-testid="takeoff-run-ingest-now"
                >
                  {ingestingFromTakeoff ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Ingesting…</>
                  ) : (
                    <>Run Ingest Now</>
                  )}
                </button>
              )}
              {doc.ingest_status === "waiting" && (
                <span className="text-[10px] font-mono text-slate-500">auto-refreshing…</span>
              )}
            </div>
          )}
        </div>

        {(doc.attachments || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="takeoff-attachments">
            {doc.attachments.map((a) => (
              <div
                key={a.attachment_id}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs ${
                  a.source === "sam_gov_ingest"
                    ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
                    : "bg-slate-100 text-slate-700"
                }`}
                title={a.source === "sam_gov_ingest" ? "Auto-seeded from the SAM.gov RFP ingest — no re-upload needed" : undefined}
              >
                <Paperclip className="w-3 h-3" />
                <span className="max-w-[200px] truncate">{a.filename}</span>
                {a.source === "sam_gov_ingest" && (
                  <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 border border-emerald-300 px-1 rounded">SAM</span>
                )}
                <button
                  onClick={() => removeAttachment(a.attachment_id)}
                  className="ml-1 p-0.5 hover:text-red-600"
                  data-testid={`takeoff-attach-remove-${a.attachment_id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Revision timeline */}
      {revisions.length > 0 && (
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm mb-6" data-testid="takeoff-revisions">
          <button
            onClick={() => setShowRevisions((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50 rounded-2xl"
            data-testid="takeoff-revisions-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center">
                <History className="w-4 h-4" />
              </div>
              <div className="text-left">
                <div className="text-[10px] font-mono font-bold tracking-widest text-violet-600">
                  // REVISION HISTORY
                </div>
                <div className="text-sm font-medium text-slate-800">
                  {revisions.length} revision{revisions.length === 1 ? "" : "s"} — current is REV {revisions[0]?.revision}
                </div>
              </div>
            </div>
            {showRevisions ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {showRevisions && (
            <div className="border-t border-slate-100 divide-y divide-slate-100">
              {revisions.map((r) => {
                const diff = r.diff_summary || {};
                const added = diff.added_sections || [];
                const removed = diff.removed_sections || [];
                const changed = diff.changed_sections || [];
                const figs = diff.figures_changed || [];
                const hasDelta = added.length || removed.length || changed.length || figs.length;
                const isOpen = openRevisionId === r.revision_id;
                return (
                  <div key={r.revision_id} className="px-5 py-3" data-testid={`rev-row-${r.revision_id}`}>
                    <div
                      className="flex items-start gap-3 cursor-pointer"
                      onClick={() => setOpenRevisionId(isOpen ? null : r.revision_id)}
                    >
                      <div
                        className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-mono font-bold ${
                          r.is_current
                            ? "bg-orange-500 text-white"
                            : "bg-slate-100 text-slate-600 border border-slate-200"
                        }`}
                      >
                        R{r.revision}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-medium text-slate-800">
                            REV {r.revision}
                            {r.is_current && (
                              <span className="ml-2 text-[9px] font-mono font-bold tracking-widest px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200">
                                CURRENT
                              </span>
                            )}
                            {(r.plan_pages_analyzed || 0) > 0 && (
                              <span className="ml-2 text-[9px] font-mono font-bold tracking-widest px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">
                                VISION · {r.plan_pages_analyzed}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 font-mono ml-auto">
                            {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                          </div>
                        </div>
                        {r.revision_note && (
                          <div className="text-xs text-slate-500 mt-0.5 italic">{r.revision_note}</div>
                        )}
                        {hasDelta ? (
                          <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                            {added.length > 0 && (
                              <span className="text-emerald-700">+ {added.length} section{added.length === 1 ? "" : "s"}</span>
                            )}
                            {removed.length > 0 && (
                              <span className="text-red-700">− {removed.length} section{removed.length === 1 ? "" : "s"}</span>
                            )}
                            {changed.length > 0 && (
                              <span className="text-blue-700">↻ {changed.length} section{changed.length === 1 ? "" : "s"} changed</span>
                            )}
                            {figs.length > 0 && (
                              <span className="text-orange-700">Σ {figs.length} figure{figs.length === 1 ? "" : "s"} updated</span>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-400 mt-1">Initial generation — nothing to compare against.</div>
                        )}
                      </div>
                    </div>
                    {isOpen && hasDelta && (
                      <div className="mt-3 ml-12 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs" data-testid={`rev-detail-${r.revision_id}`}>
                        {added.length > 0 && (
                          <div>
                            <div className="text-[9px] font-mono font-bold tracking-widest text-emerald-600 mb-1">
                              ADDED
                            </div>
                            <ul className="space-y-0.5 text-slate-700">
                              {added.map((t, i) => (
                                <li key={i}>+ {t}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {removed.length > 0 && (
                          <div>
                            <div className="text-[9px] font-mono font-bold tracking-widest text-red-600 mb-1">
                              REMOVED
                            </div>
                            <ul className="space-y-0.5 text-slate-700">
                              {removed.map((t, i) => (
                                <li key={i}>− {t}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {changed.length > 0 && (
                          <div className="md:col-span-2">
                            <div className="text-[9px] font-mono font-bold tracking-widest text-blue-600 mb-1">
                              CHANGED
                            </div>
                            <ul className="space-y-0.5 text-slate-700">
                              {changed.map((c, i) => (
                                <li key={i}>
                                  ↻ <b>{c.title}</b>
                                  {c.rows_delta ? ` · ${c.rows_delta > 0 ? "+" : ""}${c.rows_delta} row${Math.abs(c.rows_delta) === 1 ? "" : "s"}` : ""}
                                  {c.notes_delta ? ` · ${c.notes_delta > 0 ? "+" : ""}${c.notes_delta} note${Math.abs(c.notes_delta) === 1 ? "" : "s"}` : ""}
                                  {c.cells_changed ? ` · ${c.cells_changed} cell${c.cells_changed === 1 ? "" : "s"} edited` : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {figs.length > 0 && (
                          <div className="md:col-span-2">
                            <div className="text-[9px] font-mono font-bold tracking-widest text-orange-600 mb-1">
                              SUMMARY FIGURES
                            </div>
                            <ul className="space-y-0.5 text-slate-700">
                              {figs.map((f, i) => (
                                <li key={i}>
                                  <b>{f.label}</b>: {f.from} → {f.to}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Low-coverage banner — Claude reports low confidence OR commercial scope came back < 5000 SF */}
      {doc.low_coverage_flag && !generating && (
        <div className="mb-4 rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5 flex items-start gap-4" data-testid="low-coverage-banner">
          <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono font-bold tracking-widest text-amber-700 mb-1">COVERAGE LOOKS LOW</div>
            <div className="font-display font-bold text-slate-900 text-base leading-tight">Claude may have missed pages</div>
            <div className="text-sm text-slate-700 mt-1.5">
              {doc.low_coverage_reason || "The output looks smaller than expected for the described scope."}
            </div>
            {(doc.vision_budget_used != null) && (
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mt-2">
                LAST RUN · {doc.vision_budget_used} / {doc.vision_budget_total || 24} PAGES · {doc.plan_pages_analyzed || 0} RENDERED
              </div>
            )}
          </div>
          <button
            onClick={() => setMaxVisionConfirmOpen(true)}
            className="shrink-0 self-center bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-mono font-bold text-[11px] tracking-widest px-5 py-2.5 rounded-full inline-flex items-center gap-2 shadow-md transition-all"
            data-testid="max-vision-btn"
          >
            <RefreshCcw className="w-3.5 h-3.5" />
            RERUN · MAX VISION
          </button>
        </div>
      )}

      {/* Summary figures */}
      {(doc.summary_figures || []).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="takeoff-summary">
          {doc.summary_figures.slice(0, 6).map((f, i) => (
            <div key={f.label || `fig-${i}`} className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="text-2xl font-display font-black text-orange-600">{f.value}</div>
              <div className="text-xs text-slate-500 mt-1">{f.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Sections */}
      {(doc.sections || []).length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-16 text-center" data-testid="takeoff-no-sections">
          <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <div className="font-display font-bold text-lg text-slate-900 mb-1">No sections yet</div>
          <div className="text-sm text-slate-500 mb-6">
            Upload plans and hit <b>Generate Takeoff</b> — Claude drafts the whole document in ~30 seconds.
          </div>
        </div>
      ) : (
        <div className="space-y-6" data-testid="takeoff-sections">
          {doc.sections.map((sec, sIdx) => (
            <div key={sec.section_id || sIdx} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between gap-2 mb-2">
                <input
                  value={sec.title || ""}
                  onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                  className="font-display font-bold text-lg text-orange-700 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-orange-500 focus:outline-none flex-1"
                  data-testid={`sec-title-${sIdx}`}
                />
                <button
                  onClick={() => deleteSection(sIdx)}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  data-testid={`sec-delete-${sIdx}`}
                  title="Delete section"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {sec.description !== undefined && (
                <textarea
                  value={sec.description || ""}
                  onChange={(e) => updateSection(sIdx, { description: e.target.value })}
                  placeholder="Optional section description"
                  rows={2}
                  className="w-full text-sm text-slate-600 bg-transparent border border-transparent hover:border-slate-200 focus:border-orange-500 focus:outline-none rounded-lg px-2 py-1 mb-3"
                  data-testid={`sec-desc-${sIdx}`}
                />
              )}

              {/* Table (when the section has columns) */}
              {(sec.columns || []).length > 0 && (
                <div className="overflow-x-auto" data-testid={`sec-table-${sIdx}`}>
                  <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        {(sec.columns || []).map((c, cIdx) => (
                          <th
                            key={cIdx}
                            className="text-left px-3 py-2 text-[10px] font-mono font-bold tracking-widest"
                          >
                            {c}
                          </th>
                        ))}
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sec.rows || []).map((row, rIdx) => {
                        // Use section id + row content signature for a stable key so
                        // deleting a row in the middle doesn't shift the wrong
                        // controlled input state to the wrong row.
                        const rowKey = `${sec.section_id || sIdx}-r${rIdx}-${(row[0] ?? "").toString().slice(0, 16)}`;
                        return (
                        <tr key={rowKey} className="border-t border-slate-200 hover:bg-orange-50/30">
                          {(sec.columns || []).map((_, cIdx) => (
                            <td key={`${rowKey}-c${cIdx}`} className="px-2 py-1">
                              <input
                                value={row[cIdx] ?? ""}
                                onChange={(e) => updateCell(sIdx, rIdx, cIdx, e.target.value)}
                                className="w-full px-2 py-1 text-sm bg-transparent border border-transparent hover:border-slate-200 focus:border-orange-500 focus:outline-none rounded"
                                data-testid={`sec-${sIdx}-cell-${rIdx}-${cIdx}`}
                              />
                            </td>
                          ))}
                          <td className="px-1 py-1 text-right">
                            <button
                              onClick={() => deleteRow(sIdx, rIdx)}
                              className="p-1 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded"
                              data-testid={`sec-${sIdx}-row-delete-${rIdx}`}
                              title="Delete row"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <button
                    onClick={() => addRow(sIdx)}
                    className="mt-2 text-xs font-mono font-bold tracking-widest text-orange-600 hover:text-orange-800 inline-flex items-center gap-1"
                    data-testid={`sec-${sIdx}-add-row`}
                  >
                    <Plus className="w-3.5 h-3.5" /> ADD ROW
                  </button>
                </div>
              )}

              {/* Notes / bullets */}
              {(sec.notes || []).length > 0 || sec.kind === "assumptions" || sec.kind === "exclusions" || sec.kind === "verification" ? (
                <div className="mt-3 space-y-1.5" data-testid={`sec-notes-${sIdx}`}>
                  {(sec.notes || []).map((n, nIdx) => {
                    // Note keys use content prefix so deleting mid-list keeps
                    // remaining textareas locked to the correct data.
                    const noteKey = `${sec.section_id || sIdx}-n${nIdx}-${(n || "").slice(0, 24)}`;
                    return (
                    <div key={noteKey} className="flex items-start gap-2 group">
                      <span className="text-orange-500 mt-1">•</span>
                      <textarea
                        value={n}
                        onChange={(e) => updateNote(sIdx, nIdx, e.target.value)}
                        rows={1}
                        className="flex-1 text-sm text-slate-700 bg-transparent border border-transparent hover:border-slate-200 focus:border-orange-500 focus:outline-none rounded px-2 py-1 resize-none"
                        onInput={(e) => {
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }}
                        data-testid={`sec-${sIdx}-note-${nIdx}`}
                      />
                      <button
                        onClick={() => deleteNote(sIdx, nIdx)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-red-600 rounded"
                        data-testid={`sec-${sIdx}-note-delete-${nIdx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    );
                  })}
                  <button
                    onClick={() => addNote(sIdx)}
                    className="text-xs font-mono font-bold tracking-widest text-orange-600 hover:text-orange-800 inline-flex items-center gap-1"
                    data-testid={`sec-${sIdx}-add-note`}
                  >
                    <Plus className="w-3.5 h-3.5" /> ADD NOTE
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {/* Max-vision rerun confirm dialog */}
      {maxVisionConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="max-vision-confirm">
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden" style={{ backgroundColor: "#ffffff" }}>
            <div className="px-6 pt-5 pb-4 border-b border-slate-200 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                <RefreshCcw className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-mono font-bold tracking-widest text-amber-700">MAX VISION RERUN</div>
                <div className="text-lg font-display font-bold text-slate-900 leading-tight mt-0.5">Reprocess at maximum resolution?</div>
              </div>
              <button onClick={() => setMaxVisionConfirmOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="max-vision-confirm-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm text-slate-700">
              <p>
                This rerun sends up to <strong>20 pages per file</strong> (was 8), a global budget of <strong>60 pages</strong> (was 24), and accepts PDFs up to <strong>100 MB</strong>.
              </p>
              <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800">
                Uses <strong>~3× the compute</strong> of a normal generation. Best for commercial or multi-building projects where the first pass reported low coverage.
              </div>
              <p className="text-slate-500">
                Your current takeoff will be saved as revision {doc.revision || 1}. The new draft becomes revision {(doc.revision || 1) + 1}.
              </p>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
              <button
                onClick={() => setMaxVisionConfirmOpen(false)}
                className="text-sm font-mono font-bold tracking-widest text-slate-600 hover:text-slate-900"
                data-testid="max-vision-confirm-cancel"
              >CANCEL</button>
              <button
                onClick={() => { setMaxVisionConfirmOpen(false); generate({ maxVision: true }); }}
                className="px-5 py-2.5 rounded-full text-xs font-mono font-bold tracking-widest text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md flex items-center gap-2"
                data-testid="max-vision-confirm-run"
              >
                <RefreshCcw className="w-3.5 h-3.5" />RERUN
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
